'use client';

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StorefrontFormattersProvider } from '@/hooks/useStorefrontFormatters';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Clock,
  MapPin,
  Phone,
  Mail,
  Menu,
  CheckCircle,
  Building,
  AlertTriangle,
  FileText,
  Shield,
  Cookie,
  Gift,
  Images,
} from 'lucide-react';

import Impressum from '@/components/Impressum';
import StorefrontCookieBanner from '@/components/StorefrontCookieBanner';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { ThemeProvider } from '@/components/ThemeProvider';
import { AnnouncementBanner } from './AnnouncementBanner';
import { Reveal } from './_internal/Reveal';
import { Tenant, Service, BusinessHour, GalleryImage } from './types';

// ✅ CODE SPLITTING: Lazy-load heavy components (50% smaller initial bundle)
// Gallery is purely supporting content — only ship its JS when a tenant
// actually has images (guarded at the call-site below).
const GallerySection = dynamic(() => import('./GallerySection'), { ssr: true });

// Mehrfachkarten-Online-Kauf — nur Storefront-Content, lazy (rendert nichts,
// wenn der Tenant keine online-verkaufbaren Pakete hat).
const PackagePurchaseSection = dynamic(
  () => import('./PackagePurchaseSection').then((m) => m.PackagePurchaseSection),
  { ssr: false },
);

// Gutschein-Online-Kauf — rendert nichts, wenn der Tenant keine Gutscheine verkauft.
const GiftCardPurchaseSection = dynamic(
  () => import('./GiftCardPurchaseSection').then((m) => m.GiftCardPurchaseSection),
  { ssr: false },
);

const BookingSteps = dynamic(() => import('./booking/BookingSteps'), {
  loading: () => (
    <div className="flex justify-center py-12">
      <div
        className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2"
        style={{
          borderTopColor: 'var(--primary-color, #10b981)',
          borderBottomColor: 'var(--primary-color, #10b981)',
        }}
      ></div>
    </div>
  ),
  ssr: true,
});

const PrivacyPolicy = dynamic(() => import('./PrivacyPolicy'), {
  loading: () => (
    <div className="flex justify-center py-8">
      <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-gray-400"></div>
    </div>
  ),
  ssr: false,
});
import { apiManager } from '@/lib/api-manager';
import { errorMsg } from '@/lib/utils/error';

// Import utils
import {
  DEFAULT_BUSINESS_TIMEZONE,
  getBusinessTimezone,
  formatBusinessTime,
} from '@/lib/utils/business-time';
import { formatBusinessHour } from '@/lib/utils/business-hours';
import { themedTint } from './booking/_shared/tokens';

interface StorefrontPageProps {
  initialTenantId?: string;
  /** When true: strip header / footer / cookie banner / language switcher
   *  and run as an embeddable widget. Set by the `?embed=1` URL param via
   *  the StorefrontEmbedShell wrapper. */
  embed?: boolean;
}

/** Relative luminance (0 = black … 1 = white) of a #rrggbb color.
 *  Returns null for anything we can't parse, so callers keep their
 *  existing (light) behaviour when a tenant has no valid color. */
function hexLuminance(hex?: string | null): number | null {
  if (!hex) return null;
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Contrast-correct on-accent text color. Uses WCAG gamma-corrected relative
 * luminance and picks the foreground (near-black vs white) with the HIGHER
 * actual contrast ratio against the accent — not a naive luminance cutoff
 * (which mis-picked white on mid-bright accents like cyan #00bcf0 / green
 * #00c970 → unreadable CTAs). Returns white for unparseable input.
 */
function readableTextOnAccent(hex?: string | null): '#111827' | '#ffffff' {
  const m = hex ? /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim()) : null;
  if (!m) return '#ffffff';
  const relLum = (rgb: number[]): number => {
    const [r, g, b] = rgb.map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const accentL = relLum([parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]);
  const ratio = (l1: number, l2: number): number =>
    (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  // #111827 relative luminance ≈ 0.0106; #ffffff = 1.
  return ratio(accentL, 0.0106) >= ratio(accentL, 1) ? '#111827' : '#ffffff';
}

/** Live open/closed status derived from the tenant's business hours.
 *  All comparisons happen in wall-clock tenant time so DST + timezone are
 *  handled by date-fns-tz. `day` is ISO 1..7 (Mon..Sun) matching day_of_week. */
type OpenStatus =
  | { kind: 'open'; until: string }
  | { kind: 'opens_today'; at: string }
  | { kind: 'opens_day'; day: number; at: string }
  | { kind: 'closed' };

function hhmmToMinutes(hhmm: string | null): number | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

function computeOpenStatus(
  hours: BusinessHour[],
  timezone: string,
  now: Date,
): OpenStatus {
  if (!hours || hours.length === 0) return { kind: 'closed' };
  const tz = timezone || DEFAULT_BUSINESS_TIMEZONE;
  const todayDow = Number(formatInTimeZone(now, tz, 'i'));
  const nowMin = hhmmToMinutes(formatInTimeZone(now, tz, 'HH:mm')) ?? 0;
  const byDay = (dow: number) => hours.find((h) => h.day_of_week === dow) || null;
  const disp = (t?: string | null) => (t ? formatBusinessTime(t, tz, 'HH:mm') : null);

  // Yesterday's interval may spill past midnight (e.g. Fri 22:00–02:00): at
  // Sat 01:00 we are still OPEN until 02:00. This is the currently-active
  // interval, so check it BEFORE today's own hours.
  const yDow = ((todayDow - 2 + 7) % 7) + 1;
  const yesterday = byDay(yDow);
  if (yesterday?.is_open && yesterday.open_time && yesterday.close_time) {
    const yOpenStr = disp(yesterday.open_time);
    const yCloseStr = disp(yesterday.close_time);
    const yo = hhmmToMinutes(yOpenStr);
    const yc = hhmmToMinutes(yCloseStr);
    // Overnight only: close_time <= open_time means the interval wraps into
    // today, so its post-midnight tail is [00:00, close_time).
    if (yo !== null && yc !== null && yCloseStr && yc <= yo && nowMin < yc) {
      return { kind: 'open', until: yCloseStr };
    }
  }

  const today = byDay(todayDow);
  if (today?.is_open && today.open_time && today.close_time) {
    const openStr = disp(today.open_time);
    const closeStr = disp(today.close_time);
    const o = hhmmToMinutes(openStr);
    let c = hhmmToMinutes(closeStr);
    if (o !== null && c !== null && openStr && closeStr) {
      if (c <= o) c += 1440;
      if (nowMin >= o && nowMin < c) return { kind: 'open', until: closeStr };
      if (nowMin < o) return { kind: 'opens_today', at: openStr };
    }
  }
  for (let off = 1; off <= 7; off++) {
    const dow = ((todayDow - 1 + off) % 7) + 1;
    const d = byDay(dow);
    if (d?.is_open && d.open_time) {
      const at = disp(d.open_time);
      if (at) return { kind: 'opens_day', day: dow, at };
    }
  }
  return { kind: 'closed' };
}

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

/** Platform-neutral maps URL (Google Maps universal link). */
function buildMapsUrl(parts: (string | null | undefined)[]): string {
  const query = parts.filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export default function StorefrontPage({ initialTenantId, embed = false }: StorefrontPageProps) {
  // i18n hooks
  const t = useTranslations('storefront');
  const tDays = useTranslations('days');
  const tErrors = useTranslations('errors');
  const locale = useLocale();

  // State Management
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(initialTenantId || null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Stripe-Connect payment branch — populated only when the booking
  // create response carries `requiresPayment: true`. BookingSteps reads
  // this and renders <PaymentStep/> instead of the confirmation screen.
  // Stays null for ALL existing tenants (require_payment_at_booking
  // defaults to false in the DB → backend never sets the flag).
  const [pendingPayment, setPendingPayment] = useState<{
    bookingId: string;
    clientSecret: string;
    connectedAccountId: string;
    amount: number;
    currency: string;
  } | null>(null);
  // Phase 4: when create-booking returns `requiresChoice:true`,
  // BookingSteps renders <PaymentChoiceStep/> with these props.
  // Customer picks an option, we POST /confirm-payment-choice which
  // either transitions to pendingPayment (full/deposit) or directly
  // closes the booking (on_site).
  const [pendingChoice, setPendingChoice] = useState<{
    bookingId: string;
    customerEmail: string;
    serviceName: string;
    config: import('@/lib/stripe/paymentPolicy').PaymentConfig;
    cancellation: import('@/lib/stripe/paymentPolicy').CancellationPolicy;
    amounts: import('@/lib/stripe/paymentPolicy').ResolvedAmounts;
  } | null>(null);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [gallery, setGallery] = useState<GalleryImage[]>([]);
  // Gates für die Storefront-Kauf-Sections — aus dem initialen booking-data-
  // Payload, damit Tenants ohne Feature keinen Extra-Client-Fetch auslösen.
  const [hasOnlinePackages, setHasOnlinePackages] = useState(false);
  // Hat der Tenant einlösbare Gutscheine? → gate für das Code-Einlöse-Feld.
  const [hasGiftCards, setHasGiftCards] = useState(false);
  // Phase 6: F-500 transparency — payment-badge per service so customer
  // sees "Vor Ort / Anzahlung / Online" on the service-card BEFORE
  // picking. Pre-resolved server-side once when the tenant loads.
  const [paymentBadges, setPaymentBadges] = useState<Record<string, string | null>>({});
  // True sobald die Payment-Badges geladen sind → der payment-abhängige
  // Trust-Chip wird erst dann gerendert (kein Flicker/Swap).
  const [paymentBadgesLoaded, setPaymentBadgesLoaded] = useState(false);

  // Modal states
  const [showImpressum, setShowImpressum] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  // Customer-recall — hybrid: localStorage (silent autofill, default-on,
  // no consent needed → form-state caching) + server-cookie (opt-in via
  // checkbox, cross-device, rotates per-use). Server-side data wins when
  // both are present (it's authoritative + just rotated).
  //
  // The `source` flag drives UX: server-cookie → show "Willkommen zurück"
  // banner. localStorage-only → silent prefill, no banner (like browser
  // autofill — should feel automatic, not announced).
  const [recalledCustomer, setRecalledCustomer] = useState<{
    displayName: string;
    displayEmail: string | null;
    displayPhone: string | null;
    lastServiceId: string | null;
    source: 'local' | 'server';
  } | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  // Force-remount key for BookingSteps so the header logo click resets
  // the booking flow back to step 1 without a full page reload.
  const [bookingResetKey, setBookingResetKey] = useState(0);
  // Once the user leaves the "für wen?" mode step (from the service step
  // onward), BookingSteps flips this true → the big hero + gallery collapse so
  // the step overview sits at the very top and the sticky footer/CTA is
  // reachable (focused booking mode). Resets to false on reset/mode step.
  const [inActiveWizard, setInActiveWizard] = useState(false);
  // Photo-showcase: the hero doubles as the gallery. Its "Alle Fotos" button +
  // thumbnails open the shared lightbox (mounted lightbox-only via
  // <GallerySection embedded/>). We remember the trigger so focus returns to it
  // on close (a11y).
  const galleryTriggerRef = useRef<HTMLElement | null>(null);
  const openGallery = (index: number, trigger?: HTMLElement | null) => {
    if (trigger) galleryTriggerRef.current = trigger;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('eb:open-gallery', { detail: { index } }));
    }
  };
  useEffect(() => {
    const onClosed = () => galleryTriggerRef.current?.focus();
    if (typeof window === 'undefined') return;
    window.addEventListener('eb:gallery-closed', onClosed);
    return () => window.removeEventListener('eb:gallery-closed', onClosed);
  }, []);
  const resetBooking = () => {
    setBookingResetKey((k) => k + 1);
    // Restore the hero synchronously so it doesn't flicker/stay collapsed
    // between the reset and the remounted BookingSteps reporting back.
    setInActiveWizard(false);
    // Clear the top "Buchung erfolgreich!" banner — otherwise the
    // banner from the previous booking stays visible while the user
    // starts the new one, which looks like a state-leak bug.
    setSuccessMessage(null);
    if (typeof window !== 'undefined') {
      // Strip ?step=6 (or any URL param) so a reload doesn't drop the
      // user back onto an empty confirmation page.
      const url = new URL(window.location.href);
      if (url.search) {
        url.search = '';
        window.history.replaceState({}, '', url.toString());
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Initialize tenant ID
  useEffect(() => {
    if (initialTenantId) {
      setTenantId(initialTenantId);
    } else {
      const cookieId = getCookieValue('tenant_id');
      if (cookieId) {
        setTenantId(cookieId);
      }
    }
  }, [initialTenantId]);

  // Load all data when tenant ID is set
  useEffect(() => {
    if (tenantId) {
      fetchAllBookingData(tenantId);
    } else {
      setLoading(false);
      setError(t('errorLoading'));
    }
  }, [tenantId]);


  // Mehrfachkarten-Online-Kauf: Stripe leitet nach erfolgreicher Zahlung auf
  // ?purchase=success zurück. Erfolgsmeldung zeigen + Param strippen, damit ein
  // Reload sie nicht wiederholt.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('purchase') === 'success') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSuccessMessage(t('packages.purchaseSuccess'));
      url.searchParams.delete('purchase');
      window.history.replaceState({}, '', url.toString());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (url.searchParams.get('gift') === 'success') {
      setSuccessMessage(t('giftCards.purchaseSuccess'));
      url.searchParams.delete('gift');
      window.history.replaceState({}, '', url.toString());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [t]);

  // Cleanup API manager on unmount
  useEffect(() => {
    return () => {
      apiManager.cancelAllRequests();
    };
  }, []);

  // Dark storefront: paint <html>/<body> and the iOS status-bar (theme-color)
  // dark too — otherwise the safe-area / overscroll areas above and below the
  // page flash white. Scoped + restored on unmount so other pages stay light.
  useEffect(() => {
    const bg = tenant?.background_color;
    const lum = hexLuminance(bg);
    const isDark = !embed && lum !== null && lum < 0.5;
    if (!isDark || typeof document === 'undefined') return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.backgroundColor;
    const prevBody = body.style.backgroundColor;
    html.style.backgroundColor = bg as string;
    body.style.backgroundColor = bg as string;
    let meta = document.querySelector('meta[name="theme-color"]');
    const created = !meta;
    const prevMeta = meta?.getAttribute('content') ?? null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', bg as string);
    return () => {
      html.style.backgroundColor = prevHtml;
      body.style.backgroundColor = prevBody;
      if (created) meta?.remove();
      else if (prevMeta !== null) meta?.setAttribute('content', prevMeta);
    };
  }, [tenant?.background_color, embed]);

  // Cookie Banner Privacy Policy Event Listener
  useEffect(() => {
    const handleOpenPrivacyPolicy = () => {
      setShowPrivacyPolicy(true);
    };

    window.addEventListener('openPrivacyPolicy', handleOpenPrivacyPolicy);
    return () => {
      window.removeEventListener('openPrivacyPolicy', handleOpenPrivacyPolicy);
    };
  }, []);

  // Customer-recall hybrid lookup. Resolves in two passes:
  //   1) localStorage — instant, silent, no network. Powers same-device
  //      autofill (browser-autofill-style, no banner).
  //   2) Server-cookie /check — network round-trip. If hit, upgrades
  //      the recall to `source: 'server'` which shows the welcome-back
  //      banner. Server data wins because it was just rotated, lives
  //      in our authoritative store, and proves opt-in.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;

    // Pass 1: localStorage. Needs tenant SLUG, which we don't have yet
    // (tenant info loads async). Read once tenant is loaded — handled
    // in second effect below.

    // Pass 2: server-cookie. Fires regardless; harmless 204 on miss.
    // When server hits AND localStorage has data on the same device,
    // we merge the email from localStorage (which the user typed themselves
    // last time, so it's safe to expose to themselves). Server never echoes
    // email back as XSS hardening, but localStorage same-device echo is
    // identical to Browser-Autofill behaviour — no new attack surface.
    fetch(`/api/storefront/customer-recall/check?tenant_id=${encodeURIComponent(tenantId)}`, {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(async (data) => {
        if (cancelled || !data?.displayName) return;
        let displayEmail: string | null = null;
        try {
          const mod = await import('@/lib/storefront/customer-recall-local');
          const local = mod.readLocalRecallProfile(tenantId);
          if (local?.email) displayEmail = local.email;
        } catch {
          /* best-effort */
        }
        if (cancelled) return;
        setRecalledCustomer({
          displayName: data.displayName,
          displayEmail,
          displayPhone: data.displayPhone ?? null,
          lastServiceId: data.lastServiceId ?? null,
          source: 'server',
        });
      })
      .catch(() => {
        /* best-effort */
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // localStorage pass — uses tenant_id (canonical, works for slug- and
  // custom-domain tenants identically). Only sets recalled state if the
  // server pass hasn't already populated it (server data is fresher).
  useEffect(() => {
    if (!tenantId) return;
    if (recalledCustomer?.source === 'server') return;
    let cancelled = false;
    void import('@/lib/storefront/customer-recall-local').then((mod) => {
      if (cancelled) return;
      const local = mod.readLocalRecallProfile(tenantId);
      if (!local) return;
      setRecalledCustomer((prev) =>
        // Race guard: server may have arrived during the dynamic-import
        // round-trip. If so, don't downgrade.
        prev?.source === 'server'
          ? prev
          : {
              displayName: local.name,
              displayEmail: local.email,
              displayPhone: local.phone,
              lastServiceId: local.lastServiceId,
              source: 'local',
            }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, recalledCustomer?.source]);

  // Function to show cookie settings
  const openCookieSettings = () => {
    if (tenant) {
      // Clear existing consent to show banner again with settings
      localStorage.removeItem(`cookie-consent-${tenant.id}`);
      // Reload page to show cookie banner
      window.location.reload();
    }
  };

  // Helper functions
  const getCookieValue = (name: string) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  // Enterprise-level API call - Single combined endpoint
  const fetchAllBookingData = async (id: string) => {
    if (!id) {
      setError(t('noTenantId'));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use API Manager with caching and request cancellation
      const responseData = await apiManager.makeRequest(
        `booking-data-${id}`,
        async () => {
          const controller = new AbortController();
          const fetchWithController = apiManager.createFetch(controller);

          const response = await fetchWithController(`/api/storefront/booking-data?tenantId=${id}`);

          // Subscription-gate path: API returns 402 with a code field. Surface
          // it as a typed error so the page can render an offline notice
          // instead of a generic error.
          if (response.status === 402) {
            const payload = await response.json().catch(() => null);
            const err = new Error('STOREFRONT_OFFLINE') as Error & { code?: string };
            err.code = payload?.code ?? 'SUBSCRIPTION_INACTIVE';
            throw err;
          }

          if (!response.ok) {
            throw new Error(`${t('errorLoading')}: ${response.statusText}`);
          }

          return response.json();
        },
        'tenant-data' // Cache key
      );

      if (responseData.success && responseData.data) {
        const { tenant, services, businessHours, bookingStats, gallery, hasOnlinePackages, hasGiftCards } = responseData.data;

        // Set all data at once - no multiple state updates
        setTenant(tenant);
        setServices(services || []);
        setBusinessHours(businessHours || []);
        setGallery(gallery || []);
        setHasOnlinePackages(!!hasOnlinePackages);
        setHasGiftCards(!!hasGiftCards);

        // Fire-and-forget badge resolve. Failure → no badges shown,
        // booking still works (Engine resolves again at submit time).
        if (tenant?.id) {
          fetch(`/api/storefront/payment-badges?tenantId=${tenant.id}`)
            .then((r) => r.json())
            .then((d) => {
              if (d.success && d.badges) setPaymentBadges(d.badges);
            })
            .catch(() => {
              /* silent — non-essential UX hint */
            })
            // Trust-Strip-Chip erst rendern, wenn der Zahlungs-Status feststeht
            // (kein „Schnell & einfach" → „Sichere Zahlung"-Flicker). F500-Pattern.
            .finally(() => setPaymentBadgesLoaded(true));
        }
      } else {
        throw new Error('Unerwartetes API-Antwortformat');
      }
    } catch (error: unknown) {
      if (errorMsg(error) === 'Request was cancelled') {
        return; // Don't show error for cancelled requests
      }
      // Subscription-gate fall-through — `setOffline` shows the offline
      // notice instead of the generic error state.
      const codedError = error as Error & { code?: string };
      if (codedError?.message === 'STOREFRONT_OFFLINE') {
        setOffline(codedError.code ?? 'SUBSCRIPTION_INACTIVE');
        return;
      }
      setError(errorMsg(error) || tErrors('generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (formData: {
    service: Service;
    employee?: { id: string; first_name: string; last_name: string } | null;
    date: Date;
    time: string;
    customerName: string;
    customerEmail: string;
    customerPhone: string;
    customerComment: string;
    giftCardCode?: string;
    intakeAnswers?: Record<string, unknown>;
    intakeConsent?: boolean;
    rememberMe?: boolean;
    /** Optional additional persons piggy-backing on the primary
     *  customer. When non-empty, hits the group-create endpoint instead
     *  of the single-create endpoint. The conflict-check below stays the
     *  same — group endpoint also validates server-side. */
    additionalPersons?: Array<{
      serviceId: string;
      serviceName: string;
      bookingTime: string;
      duration: number;
      employeeProfileId: string | null;
      guestName?: string;
      guestAgeCategory?: 'adult' | 'child' | 'senior' | null;
    }>;
  }) => {
    try {
      if (!tenant?.id) {
        throw new Error(t('noTenantId'));
      }

      // SENIOR SOLUTION: Send date and time as pure strings to API
      // Let the API handle ALL timezone conversion - zero client-side timezone logic
      const businessTimezone = tenant?.timezone || DEFAULT_BUSINESS_TIMEZONE;
      const dateString = formatInTimeZone(formData.date, businessTimezone, 'yyyy-MM-dd'); // Use tenant timezone
      const timeString = formData.time; // Just the time part "14:30"

      // API gets: date="2025-08-06", time="14:30" - API treats as German business time

      // ENTERPRISE FEATURE: Real-time conflict detection before booking
      const conflictCheck = await apiManager.makeRequest(
        `conflict-check-${tenant.id}-${formData.service.id}-${formData.time}`,
        async () => {
          const controller = new AbortController();
          const fetchWithController = apiManager.createFetch(controller);

          const response = await fetchWithController('/api/storefront/booking-data', {
            method: 'POST',
            body: JSON.stringify({
              tenantId: tenant.id,
              serviceId: formData.service.id,
              date: dateString,
              time: formData.time,
            }),
          });

          if (!response.ok) {
            throw new Error(t('availabilityCheckError'));
          }

          return response.json();
        }
      );

      if (!conflictCheck.available) {
        throw new Error(t('conflictError'));
      }

      // Group-booking branch — when the customer added one or more
      // additional persons via the inline GroupAddPanel, route the
      // payload to the atomic group-create endpoint. The single-booking
      // create path below stays untouched for the typical solo flow.
      const isGroup =
        Array.isArray(formData.additionalPersons) &&
        formData.additionalPersons.length > 0;

      const response = isGroup
        ? await fetch('/api/storefront/booking-groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tenantId: tenant.id,
              primaryCustomerName: formData.customerName,
              primaryCustomerEmail: formData.customerEmail,
              primaryCustomerPhone: formData.customerPhone,
              locale,
              groupType: 'group',
              allocationMode: 'sequential',
              notes: formData.customerComment || null,
              bookings: [
                {
                  service_id: formData.service.id,
                  service_name: formData.service.name,
                  booking_time: `${dateString}T${timeString}:00`,
                  duration: formData.service.duration,
                  employee_profile_id: formData.employee?.id || null,
                },
                ...(formData.additionalPersons ?? []).map((p) => ({
                  service_id: p.serviceId,
                  service_name: p.serviceName,
                  booking_time: p.bookingTime,
                  duration: p.duration,
                  employee_profile_id: p.employeeProfileId,
                  guest_name: p.guestName,
                  guest_age_category: p.guestAgeCategory ?? null,
                })),
              ],
            }),
          })
        : await fetch('/api/storefront/create-booking', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              tenantId: tenant.id,
              serviceId: formData.service.id,
              serviceName: formData.service.name,
              employeeId: formData.employee?.id || null,
              customerName: formData.customerName,
              customerEmail: formData.customerEmail,
              customerPhone: formData.customerPhone,
              customerComment: formData.customerComment,
              giftCardCode: formData.giftCardCode || undefined,
              intakeAnswers: formData.intakeAnswers,
              intakeConsent: formData.intakeConsent,
              bookingTime: `${dateString}T${timeString}:00`,
              duration: formData.service.duration,
              locale,
            }),
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({} as Record<string, unknown>));

        // Map known error codes to friendly messages. Falls back to
        // the server-provided message (or a generic) for unknown codes.
        // No more dumping internal flags / headers into the toast.
        const code = (errorData as { code?: string }).code;
        const conflictingSlot =
          (errorData as { details?: { conflictingSlot?: string } }).details
            ?.conflictingSlot ?? null;
        const friendlyMessages: Record<string, string> = {
          SLOT_CONFLICT:
            'Dieser Zeitslot ist nicht mehr verfügbar. Bitte wähle eine andere Uhrzeit.',
          GROUP_TOO_LARGE: 'Maximal 10 Personen pro Gruppenbuchung.',
          EMPTY_GROUP: 'Mindestens eine Person muss ausgewählt sein.',
          SUBSCRIPTION_INACTIVE:
            'Diese Buchungsseite ist derzeit nicht verfügbar.',
          RATE_LIMITED:
            'Zu viele Anfragen. Bitte versuche es in einer Minute erneut.',
        };
        const errorMessage =
          (code && friendlyMessages[code]) ||
          (errorData as { message?: string }).message ||
          t('bookingError');

        // Side-channel — pass the conflicting slot to the catch-block via
        // a custom error property so the UI can offer "andere Zeit
        // wählen" navigation.
        const err = new Error(errorMessage) as Error & {
          code?: string;
          conflictingSlot?: string;
        };
        if (code) err.code = code;
        if (conflictingSlot) err.conflictingSlot = conflictingSlot;
        throw err;
      }

      const data = await response.json();

      // Clear cache after successful booking to ensure fresh data
      apiManager.clearCache(['available-slots', 'booking-stats']);

      // Phase 4: Multi-option flow — customer picks via PaymentChoiceStep.
      if (data.requiresChoice && data.choice && data.booking?.id) {
        setPendingChoice({
          bookingId: data.booking.id,
          customerEmail: formData.customerEmail,
          serviceName: formData.service.name,
          config: data.choice.config,
          cancellation: data.choice.cancellation,
          amounts: data.choice.amounts,
        });
        return;
      }
      // Phase 3 Stripe-Connect branch — additive, no impact on
      // tenants who haven't opted into customer-payments.
      if (data.requiresPayment && data.payment && data.booking?.id) {
        setPendingPayment({
          bookingId: data.booking.id,
          clientSecret: data.payment.clientSecret,
          connectedAccountId: data.payment.connectedAccountId,
          amount: data.payment.amount,
          currency: data.payment.currency,
        });
        // Don't set the success-message yet — confirmation only happens
        // after payment_intent.succeeded webhook flips the booking.
        return;
      }

      {
        const baseMsg = data.booking?.calendar_event_id
          ? t('bookingSuccessWithCalendar', { name: formData.customerName })
          : t('bookingSuccess', { name: formData.customerName });
        // Mehrfachkarte / Gutschein eingelöst → Restguthaben anzeigen.
        let extra = '';
        if (data.pass) {
          extra = ` ${t('passRemaining', { remaining: data.pass.remaining, total: data.pass.total })}`;
        } else if (data.giftCard) {
          const remaining = (data.giftCard.remainingCents / 100).toLocaleString(locale, {
            style: 'currency',
            currency: (data.giftCard.currency || 'eur').toUpperCase(),
          });
          extra = ` ${t('giftCards.redeemed', { remaining })}`;
        }
        setSuccessMessage(`${baseMsg}${extra}`);
      }

      // Customer-recall hybrid persistence. ALWAYS write the local
      // profile (form-state caching, no consent needed — same legal
      // category as Browser-Autofill). ADDITIONALLY issue a server-side
      // recall token IF the user explicitly opted in via the "Auch
      // andere Geräte" checkbox (cross-device persistence, requires
      // consent under DSGVO).
      try {
        const { saveLocalRecallProfile } = await import(
          '@/lib/storefront/customer-recall-local'
        );
        if (tenantId) {
          // Capture a group snapshot only when the customer just booked
          // as a group. Solo bookings clear any previous snapshot so
          // they're not haunted by an old group when they're now alone.
          const groupSnapshot =
            isGroup && Array.isArray(formData.additionalPersons) && formData.additionalPersons.length > 0
              ? {
                  size: formData.additionalPersons.length + 1,
                  // The submit branch above hard-codes 'sequential' for
                  // the group endpoint — mirror that here. (Phase 4
                  // could thread the real allocationMode through.)
                  allocationMode: 'sequential' as const,
                  members: [
                    {
                      guestName: undefined, // primary
                      serviceId: formData.service.id ?? null,
                      serviceName: formData.service.name,
                    },
                    ...formData.additionalPersons.map((p) => ({
                      guestName: p.guestName,
                      serviceId: p.serviceId,
                      serviceName: p.serviceName,
                    })),
                  ],
                  ts: Date.now(),
                }
              : null;

          saveLocalRecallProfile(tenantId, {
            name: formData.customerName,
            email: formData.customerEmail,
            phone: formData.customerPhone,
            lastServiceId: formData.service.id ?? null,
            lastGroup: groupSnapshot,
          });
        }
      } catch {
        /* best-effort */
      }
      if (formData.rememberMe && data.booking?.id && tenantId) {
        try {
          await fetch('/api/storefront/customer-recall/issue', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tenantId,
              bookingId: data.booking.id,
            }),
            credentials: 'include',
          });
        } catch {
          /* best-effort — booking succeeded, recall is nice-to-have */
        }
      }

      // Cross-frame booking-completed signal — StorefrontEmbedShell picks
      // this up and forwards via postMessage to the parent page. No-op
      // when the page isn't embedded (no listener mounted). The price
      // value is read from the service so the host's tracking pixel can
      // fire `purchase` with an accurate revenue number.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('eazybooking:internal:booking_completed', {
            detail: {
              bookingId: data.booking?.id ?? data.bookingId ?? 'unknown',
              value:
                typeof formData.service.price === 'number'
                  ? formData.service.price
                  : undefined,
              // Currency-of-record: prefer the per-service snapshot,
              // fall back to the tenant's display currency for legacy
              // services that pre-date the per-row column.
              currency:
                (formData.service as unknown as { currency?: string }).currency
                || (tenant as unknown as { currency?: string }).currency
                || 'EUR',
            },
          })
        );
      }
    } catch (err: unknown) {
      const typed = err as Error & { code?: string };
      // SLOT_CONFLICT is a recoverable user-action error — show as a
      // toast and let the user keep their progress, rather than blowing
      // up the whole page with a fatal error state.
      if (typed.code === 'SLOT_CONFLICT') {
        const message = typed.message || t('bookingError');
        try {
          const { toast } = await import('@/hooks/use-toast');
          toast({
            title: 'Slot nicht verfügbar',
            description: message,
            variant: 'destructive',
          });
        } catch {
          if (typeof window !== 'undefined') window.alert(message);
        }
        return;
      }
      setError(errorMsg(err) || t('bookingError'));
    }
  };

  // Format business hours with i18n support
  const formatBusinessHourForDisplay = (hour: BusinessHour) => {
    const businessTimezone = getBusinessTimezone(tenant?.timezone);
    const formattedString = formatBusinessHour(
      hour,
      businessTimezone,
      (key: string) => {
        if (key.startsWith('days.')) {
          return tDays(key.replace('days.', ''));
        }
        if (key.startsWith('storefront.')) {
          return t(key.replace('storefront.', ''));
        }
        return t(key);
      }
    );

    // Parse the formatted string to extract day and hours
    const [day, hours] = formattedString.split(': ');

    return {
      day,
      hours,
      isOpen: hour.is_open,
    };
  };

  // "Powered by EazyBooking" — geteilte Markup für Footer UND Loading-State.
  // Wird im Loading-Zweig mitgerendert, weil StorefrontPage ein Client-
  // Component ist und beim SSR mit loading=true startet: nur so landet der
  // Backlink im initialen HTML (auch für Crawler ohne JS-Rendering / KI-Bots),
  // statt erst nach der Client-Hydration zu erscheinen. In Embed-Ansichten
  // (Chrome gestrippt) wird er weggelassen.
  const poweredByLink = (
    <a
      href="https://eazybooking.de/?source=storefront_footer"
      target="_blank"
      rel="noopener"
      className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 transition-colors hover:text-gray-600"
    >
      <span>Powered by</span>
      <span className="font-semibold text-gray-600">EazyBooking</span>
    </a>
  );

  // Loading state — clean skeleton with subtle themed spinner
  if (loading) {
    // Use CSS variable so EmbedShell theme-postMessage overrides take
    // effect immediately — ThemeProvider sets `--primary-color` on :root
    // from tenant.primary_color, and EmbedShell overrides the same var
    // when the host page sends a theme-override message.
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-5 h-10 w-10 animate-spin rounded-full border-2 border-gray-200" style={{ borderTopColor: 'var(--primary-color, #10b981)' }} />
          <h3 className="text-base font-semibold tracking-tight text-gray-900">
            {t('loadingServices')}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{t('common.loading')}</p>
        </div>
        {/* SSR-Backlink: im initialen HTML vorhanden, noch bevor Tenant-Daten
            client-seitig geladen sind. Nach der Hydration übernimmt der
            identische Link im echten Footer. */}
        {!embed && (
          <div className="absolute inset-x-0 bottom-6 flex justify-center">
            {poweredByLink}
          </div>
        )}
      </div>
    );
  }

  // Storefront offline — trial expired or subscription canceled. Render a
  // calm, customer-facing notice (NOT an error) so the visitor doesn't
  // think the site is broken.
  if (offline) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
          <div className="p-6 sm:p-8">
            <div className="mx-auto mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100">
              <AlertTriangle className="h-5 w-5 text-gray-600" strokeWidth={2} />
            </div>
            <h2 className="text-center text-[22px] font-semibold leading-7 tracking-tight text-gray-950">
              Buchungsseite derzeit nicht verfügbar
            </h2>
            <p className="mt-3 text-center text-sm leading-relaxed text-gray-600">
              Diese Buchungsseite ist gerade offline. Bitte kontaktiere den Anbieter
              direkt für einen Termin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]">
          <div className="p-6 sm:p-8">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50">
              <AlertTriangle className="h-5 w-5 text-rose-600" strokeWidth={2} />
            </div>
            <h2 className="text-center text-[22px] font-semibold leading-7 tracking-tight text-gray-900">
              Ein Fehler ist aufgetreten
            </h2>
            <div className="mt-4 rounded-2xl bg-rose-50 px-4 py-3">
              <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-rose-800">
                {error}
              </pre>
            </div>
            <Button
              onClick={() => tenantId && fetchAllBookingData(tenantId)}
              className="mt-5 h-11 w-full rounded-full"
            >
              Erneut versuchen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No tenant state
  if (!tenant) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md overflow-hidden rounded-3xl border border-gray-200/80 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)] p-6 text-center sm:p-8">
          <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100">
            <Building className="h-5 w-5 text-gray-600" strokeWidth={2} />
          </div>
          <h2 className="text-[22px] font-semibold leading-7 tracking-tight text-gray-900">
            Betrieb nicht gefunden
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            Der angeforderte Betrieb konnte nicht gefunden werden.
          </p>
        </div>
      </div>
    );
  }

  // Schema.org JSON-LD — only in non-embed mode (Google doesn't index
  // iframes, plus the host page provides its own structured-data).
  // Rendered client-side after data fetch; modern Googlebot executes JS
  // and reads the resulting structured-data correctly per Google's
  // own docs (Search Central → "JavaScript SEO").
  let schemaJson: string | null = null;
  if (!embed && tenant && services.length > 0) {
    try {
      // Inline dynamic import would force a Promise — schema is a pure
      // synchronous transform so we require at module scope above is
      // fine. Wrapped in try so any malformed tenant data degrades
      // gracefully (storefront still works without rich-snippets).
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { buildLocalBusinessSchema, resolveCanonicalUrl } = require('@/lib/storefront/schema');
      const canonicalUrl = resolveCanonicalUrl(tenant.slug, tenant.domain);
      const schema = buildLocalBusinessSchema({
        tenant,
        services,
        businessHours,
        canonicalUrl,
      });
      schemaJson = JSON.stringify(schema);
    } catch {
      /* schema generation is best-effort */
    }
  }

  // Dark storefront theme — opt-in per tenant via a dark background_color.
  // Light tenants get `isDarkTheme = false` → no data-attr, no CSS vars →
  // rendering is byte-for-byte unchanged. Scoped, never global.
  const bgLum = hexLuminance(tenant.background_color);
  // Embed widgets stay transparent/neutral — never force the dark page bg
  // inside a host site (Codex A1).
  const isDarkTheme = !embed && bgLum !== null && bgLum < 0.5;
  // Dark mode is driven by a SINGLE value: a dark `background_color`. Card and
  // text use safe dark defaults (so a tenant can enable dark with one toggle
  // without having to hand-tune secondary/text). The page background itself
  // honours the chosen shade, and `primary_color` stays the accent.
  const accent = tenant.primary_color || '#10b981';
  const cardBorderBase = isDarkTheme ? '#2a2a2a' : '#e5e7eb';
  // Accent readability guard — BOTH directions: on light surfaces a near-white
  // accent vanishes; on dark surfaces a near-black accent vanishes.
  const accentLum = hexLuminance(accent);
  const accentReadable =
    accentLum === null ? true : isDarkTheme ? accentLum > 0.3 : accentLum < 0.82;
  const accentText = accentReadable ? accent : isDarkTheme ? '#f5f5f5' : undefined;
  // On-accent foreground — text/icons that sit ON a filled accent surface
  // (CTAs, selected chips, radio checks). WCAG contrast-correct: picks
  // near-black vs white by actual contrast ratio against the accent, so a
  // light accent (#FDE047 yellow, mint) OR a mid-bright one (cyan #00bcf0,
  // green #00c970) both get readable text. Exposed as an inline var scoped to
  // the storefront subtree — NEVER leaks to admin. Dark accents still yield
  // white → existing tenants unchanged.
  const onAccent = readableTextOnAccent(accent);
  // Contact can hold multiple numbers (mobile + landline) separated by , ; / |.
  // Header/menu show the first; the contact card lists each as its own tel link.
  const phoneList = (tenant.contact_phone || '')
    .split(/[,;/|]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const primaryPhone = phoneList[0] || null;
  // Root CSS vars available in BOTH modes: subtle accent-tinted card border
  // (14% mix — visible branding without going garish on colourful tenants),
  // and in dark mode the surface/text palette.
  const rootStyle = {
    ['--sf-accent']: accent,
    ['--sf-accent-border']: `color-mix(in srgb, ${accent} 14%, ${cardBorderBase})`,
    // Contrast-safe on-accent foreground — set in BOTH modes (light-mode
    // default resolves to white → unchanged for existing tenants).
    ['--sf-on-accent']: onAccent,
    ...(isDarkTheme
      ? {
          ['--sf-bg']: tenant.background_color || '#0e0e0e',
          ['--sf-card']: '#171717',
          ['--sf-text']: '#f5f5f5',
          // Muted body/label text on dark surfaces (matches the gray-500
          // remap in globals.css so StepHeader/mobile-stepper labels align).
          ['--sf-muted']: '#a3a3a3',
          // Price accent, guarded so a near-black accent stays visible on dark.
          ['--sf-price-color']: accentReadable ? accent : '#f5f5f5',
          // NOTE: --sf-step-* (stepper palette) + the bg-white/text-gray-*
          // remaps are already defined in globals.css under
          // [data-sf-theme='dark'] — do not duplicate them here (inline vars
          // would override the shared values).
        }
      : {}),
  } as React.CSSProperties;

  // Business type in the tenant's accent color (all tenants), with the same
  // readability guard so it never vanishes on either surface.
  const businessTypeColor = accentText;

  // RTL locales (Arabic) — flip the mobile-menu Sheet + logical spacing.
  const isRtl = locale === 'ar' || locale.startsWith('ar-');

  // Week-start honouring the tenant's regional setting (0 = Sunday-first).
  const firstDayOfWeek: 0 | 1 =
    (tenant as { first_day_of_week?: 0 | 1 })?.first_day_of_week === 0 ? 0 : 1;
  const dayOrder =
    firstDayOfWeek === 0 ? [7, 1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5, 6, 7];
  const heroTimezone = getBusinessTimezone(tenant.timezone);
  const todayDow = Number(formatInTimeZone(new Date(), heroTimezone, 'i'));

  // Live open/closed status for the hero + hours card.
  const openStatus = computeOpenStatus(businessHours, heroTimezone, new Date());
  const statusText = ((): string => {
    switch (openStatus.kind) {
      case 'open':
        return `${t('openNow')} · ${t('closesAt', { time: openStatus.until })}`;
      case 'opens_today':
        return `${t('closed')} · ${t('opensToday', { time: openStatus.at })}`;
      case 'opens_day':
        return `${t('closed')} · ${t('opensAt', {
          day: tDays(DAY_KEYS[openStatus.day - 1]),
          time: openStatus.at,
        })}`;
      default:
        return t('closed');
    }
  })();
  const isOpenNow = openStatus.kind === 'open';
  const hasStatus = businessHours.length > 0;

  // Small reusable status badge (calm green when open, muted otherwise).
  const StatusBadge = ({ className = '' }: { className?: string }) =>
    !hasStatus ? null : (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium ${
          isOpenNow ? 'text-emerald-600' : 'text-gray-500'
        } ${className}`}
      >
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: 'currentColor' }}
        />
        {statusText}
      </span>
    );

  // Address -> platform-neutral maps URL + single-line label.
  const fullAddress = tenant.address
    ? buildMapsUrl([
        tenant.address,
        tenant.address_additional,
        `${tenant.zip ?? ''} ${tenant.city ?? ''}`.trim(),
        tenant.state,
      ])
    : null;
  const cityLine = [tenant.zip, tenant.city].filter(Boolean).join(' ');
  const locationLine = [tenant.address, cityLine].filter(Boolean).join(' · ');

  // Hero cover — first gallery image if present, otherwise an accent band.
  const coverUrl = gallery.length > 0 ? gallery[0].image_url : null;

  return (
    <StorefrontFormattersProvider
      tenant={{
        currency: (tenant as { currency?: string | null }).currency,
        default_locale: tenant.default_locale,
        timezone: tenant.timezone,
      }}
    >
    <ThemeProvider
      theme={{
        primary_color: tenant.primary_color || '#8b5cf6',
        secondary_color: tenant.secondary_color || '#f8fafc',
        text_color: tenant.text_color || '#1e293b',
      }}
    >
      {/* Schema.org LocalBusiness markup — rich-snippets in search. */}
      {schemaJson && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: schemaJson }}
        />
      )}
      <div
        className={embed ? 'bg-transparent' : 'min-h-screen bg-gray-50'}
        data-sf-theme={isDarkTheme ? 'dark' : undefined}
        style={rootStyle}
      >
        {/* Header — hidden in embed mode (parent site has its own chrome). */}
        {!embed && (
        <header className="sticky top-0 z-40 border-b border-gray-200/70 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/70">
          <div className="container mx-auto px-4 py-3 md:py-3.5">
            <div className="flex items-center justify-between gap-3">
              {/* Logo + Name — clicking jumps back to step 1 of the booking
                  flow (no page reload, no redirect to the EazyBooking landing) */}
              <button
                type="button"
                onClick={resetBooking}
                className="flex min-w-0 flex-1 items-center gap-3 no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded-2xl"
              >
                {tenant.logo_url ? (
                  <div className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-2xl bg-white p-1 ring-1 ring-gray-200/80 md:h-12 md:w-12">
                    <Image
                      src={tenant.logo_url}
                      alt={`${tenant.name} Logo`}
                      width={48}
                      height={48}
                      sizes="48px"
                      className="h-full w-full object-contain"
                    />
                  </div>
                ) : (
                  <div
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl text-lg font-semibold md:h-12 md:w-12 md:text-xl"
                    style={{
                      backgroundColor: 'var(--primary-color, #10b981)',
                      color: 'var(--sf-on-accent, #fff)',
                    }}
                  >
                    {tenant.name.charAt(0)}
                  </div>
                )}
                <div className="flex min-w-0 flex-col text-left">
                  <h1 className="truncate text-[15px] font-semibold tracking-tight text-gray-900 md:text-base">
                    {tenant.name}
                  </h1>
                  {tenant.business_type && (
                    <span
                      className={`truncate text-xs font-medium ${businessTypeColor ? '' : 'text-gray-500'}`}
                      style={businessTypeColor ? { color: businessTypeColor } : undefined}
                    >
                      {tenant.business_type}
                    </span>
                  )}
                </div>
              </button>

              {/* Right cluster */}
              <div className="flex flex-shrink-0 items-center gap-2 md:gap-3">
                {/* Gutschein-CTA — dezenter Ghost-Button, auf ALLEN Breakpoints
                    sichtbar (löst die Mobile-Discoverability, wo die Sidebar
                    unter den Wizard rutscht). Scrollt zur Gutschein-Section.
                    Nur wenn der Tenant Gutscheine aktiviert hat. */}
                {(tenant as { gift_cards_enabled?: boolean }).gift_cards_enabled && (
                  <button
                    type="button"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('eb:open-gift-cards'));
                      document
                        .getElementById('gift-cards')
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    <Gift className="h-4 w-4" strokeWidth={2} aria-hidden />
                    <span className="whitespace-nowrap">{t('giftCards.headerCta')}</span>
                  </button>
                )}

                {primaryPhone && (
                  <a
                    href={`tel:${primaryPhone.replace(/\s+/g, '')}`}
                    className="hidden items-center gap-2 rounded-full px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-100 lg:flex"
                  >
                    <Phone className="h-4 w-4" strokeWidth={2} />
                    <span className="tabular-nums">{primaryPhone}</span>
                  </a>
                )}

                <LanguageSwitcher />

                {/* Mobile menu */}
                <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                  <SheetTrigger asChild>
                    <button
                      type="button"
                      aria-label="Menu"
                      className="flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 md:hidden"
                    >
                      <Menu className="h-5 w-5" strokeWidth={2} />
                    </button>
                  </SheetTrigger>
                  <SheetContent
                    side={isRtl ? 'left' : 'right'}
                    className="w-80"
                    data-sf-theme={isDarkTheme ? 'dark' : undefined}
                    style={
                      isDarkTheme
                        ? ({
                            ['--sf-card']: '#171717',
                            ['--sf-text']: '#f5f5f5',
                            backgroundColor: '#0e0e0e',
                          } as React.CSSProperties)
                        : undefined
                    }
                  >
                    <SheetHeader className="border-b pb-4">
                      <div className="flex items-center gap-3">
                        {tenant.logo_url ? (
                          <div className="relative h-10 w-10 flex-shrink-0 overflow-hidden rounded-xl bg-white p-1 ring-1 ring-gray-200/80">
                            <Image
                              src={tenant.logo_url}
                              alt={`${tenant.name} Logo`}
                              width={40}
                              height={40}
                              sizes="40px"
                              className="h-full w-full object-contain"
                            />
                          </div>
                        ) : (
                          <div
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-base font-semibold"
                            style={{
                              backgroundColor: 'var(--primary-color, #10b981)',
                              color: 'var(--sf-on-accent, #fff)',
                            }}
                          >
                            {tenant.name.charAt(0)}
                          </div>
                        )}
                        <SheetTitle className="text-left text-base font-semibold tracking-tight">
                          {tenant.name}
                        </SheetTitle>
                      </div>
                    </SheetHeader>
                    <div className="mt-6 space-y-6">
                      {/* Contact */}
                      <div className="space-y-3">
                        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                          {t('contact')}
                        </h3>
                        {phoneList.map((num) => (
                          <a
                            key={num}
                            href={`tel:${num.replace(/\s+/g, '')}`}
                            className="flex items-center gap-3 rounded-xl px-2 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-50"
                          >
                            <Phone className="h-4 w-4 text-gray-400" strokeWidth={2} />
                            <span className="break-all tabular-nums">{num}</span>
                          </a>
                        ))}
                        {tenant.contact_email && (
                          <a
                            href={`mailto:${tenant.contact_email}`}
                            className="flex items-center gap-3 rounded-xl px-2 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-50"
                          >
                            <Mail className="h-4 w-4 text-gray-400" strokeWidth={2} />
                            <span className="break-all">{tenant.contact_email}</span>
                          </a>
                        )}
                        {tenant.address && fullAddress && (
                          <a
                            href={fullAddress}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-3 rounded-xl px-2 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-50"
                          >
                            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" strokeWidth={2} />
                            <span className="break-words">
                              {tenant.address}
                              {tenant.address_additional && (
                                <>
                                  <br />
                                  {tenant.address_additional}
                                </>
                              )}
                              <br />
                              {tenant.zip} {tenant.city}
                            </span>
                          </a>
                        )}
                      </div>

                      <Separator />

                      <div className="flex justify-center">
                        <LanguageSwitcher />
                      </div>

                      <Separator />

                      {/* Legal */}
                      <div className="space-y-1">
                        <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                          {t('legal')}
                        </h3>
                        <button
                          type="button"
                          onClick={() => {
                            setShowImpressum(true);
                            setMobileMenuOpen(false);
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          <FileText className="h-4 w-4 text-gray-400" strokeWidth={2} />
                          {t('impressum')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowPrivacyPolicy(true);
                            setMobileMenuOpen(false);
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          <Shield className="h-4 w-4 text-gray-400" strokeWidth={2} />
                          {t('privacy')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            openCookieSettings();
                          }}
                          className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          <Cookie className="h-4 w-4 text-gray-400" strokeWidth={2} />
                          {t('cookieSettings')}
                        </button>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </header>
        )}

        {/* Success Message */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="container mx-auto px-4 pt-4"
            >
              {/* Success banner re-tinted from the old emerald/green to the
                  tenant accent + neutral text so it reads consistently with
                  the rest of the storefront (accent only for the check) and
                  stays readable on dark tenants via the --sf-* tokens. */}
              <Alert
                style={{
                  borderColor: 'var(--sf-accent-border, #e5e7eb)',
                  backgroundColor: `color-mix(in srgb, ${accent} 8%, transparent)`,
                }}
              >
                <CheckCircle className="h-4 w-4" style={{ color: accent }} />
                <AlertDescription style={{ color: 'var(--sf-text, #111827)' }}>
                  {successMessage}
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-6 md:py-8">
          {/* Hero band — emotional first impression. Cover image (first gallery
              photo) behind a legibility scrim, or an accent gradient when the
              tenant has no imagery. Logo, name, type, live open-status, location.
              Collapses once the user is deep in the wizard (date onward) so the
              step overview moves to the top. */}
          {!embed && !inActiveWizard && (
            <Reveal className="mx-auto mb-6 block w-full max-w-5xl md:mb-8" delay={0}>
            <section className="md:grid md:grid-cols-[1.4fr_1fr] md:gap-2.5">
              {/* LEFT — info hero over the (first) gallery photo. Doubles as the
                  gallery: the "Alle Fotos" button + the desktop thumbnails open
                  the shared lightbox, so there is no separate gallery card below
                  (saves a whole section of vertical space → booking is higher). */}
              <div
                className="relative isolate flex min-h-[200px] flex-col justify-end overflow-hidden rounded-3xl border sm:min-h-[240px] md:min-h-[320px] lg:min-h-[360px]"
                style={{ borderColor: 'var(--sf-accent-border)' }}
              >
                {coverUrl ? (
                  <>
                    <Image
                      src={coverUrl}
                      alt=""
                      aria-hidden
                      fill
                      priority
                      sizes="(max-width: 768px) 100vw, 600px"
                      className="-z-10 object-cover"
                    />
                    <div
                      aria-hidden
                      className="absolute inset-0 -z-10"
                      style={{
                        background:
                          'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.72) 100%)',
                      }}
                    />
                  </>
                ) : (
                  <div
                    aria-hidden
                    className="absolute inset-0 -z-10"
                    style={{ backgroundColor: 'var(--sf-card, #ffffff)' }}
                  />
                )}

                <div className="relative flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:gap-5 sm:p-8">
                  {tenant.logo_url ? (
                    <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-2xl bg-white p-2 shadow-sm ring-1 ring-black/5 sm:h-20 sm:w-20">
                      <Image
                        src={tenant.logo_url}
                        alt={`${tenant.name} Logo`}
                        fill
                        sizes="80px"
                        className="object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-2xl font-semibold shadow-sm ring-1 ring-black/5 sm:h-20 sm:w-20 sm:text-3xl"
                      style={{
                        backgroundColor: 'var(--primary-color, #10b981)',
                        color: 'var(--sf-on-accent, #fff)',
                      }}
                    >
                      {tenant.name.charAt(0)}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <h1
                      className="text-2xl font-semibold tracking-tight sm:text-3xl"
                      style={{
                        color: coverUrl ? '#ffffff' : 'var(--sf-text, #111827)',
                      }}
                    >
                      {tenant.name}
                    </h1>
                    {tenant.business_type && (
                      <p
                        className="mt-0.5 text-sm font-medium"
                        style={{
                          color: coverUrl
                            ? 'rgba(255,255,255,0.9)'
                            : businessTypeColor || 'var(--sf-muted, #6b7280)',
                        }}
                      >
                        {tenant.business_type}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                      {hasStatus &&
                        (coverUrl ? (
                          <span
                            className="inline-flex items-center gap-1.5 text-xs font-medium"
                            style={{
                              color: isOpenNow ? '#6ee7b7' : 'rgba(255,255,255,0.85)',
                            }}
                          >
                            <span
                              aria-hidden
                              className="h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: 'currentColor' }}
                            />
                            {statusText}
                          </span>
                        ) : (
                          <StatusBadge />
                        ))}
                      {locationLine && (
                        <span
                          className="inline-flex min-w-0 items-center gap-1.5 text-xs"
                          style={{
                            color: coverUrl
                              ? 'rgba(255,255,255,0.85)'
                              : 'var(--sf-muted, #6b7280)',
                          }}
                        >
                          <MapPin className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2} />
                          <span className="truncate">{locationLine}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Alle Fotos — mobile (and ≤4 images on desktop). Opens the
                    shared lightbox; from 5 images desktop uses the thumbnails. */}
                {coverUrl && gallery.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => openGallery(0, e.currentTarget)}
                    aria-label={`${t('gallery.viewAll')} — ${tenant.name} (${gallery.length})`}
                    className={`absolute end-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3.5 py-2 text-sm font-semibold text-white shadow-lg ring-1 ring-white/15 backdrop-blur-md transition-colors hover:bg-black/65 ${
                      gallery.length >= 5 ? 'md:hidden' : ''
                    }`}
                  >
                    <Images className="h-4 w-4" strokeWidth={2} aria-hidden />
                    {t('gallery.viewAll')}
                    {gallery.length > 1 && (
                      <span className="opacity-80">· {gallery.length}</span>
                    )}
                  </button>
                )}
              </div>

              {/* RIGHT — 2×2 thumbnails (Airbnb split) from 5 images, desktop
                  only. Each opens the lightbox at its image; the 4th carries a
                  "+N" overlay when there are more. */}
              {coverUrl && gallery.length >= 5 && (
                <div className="hidden md:grid md:grid-cols-2 md:grid-rows-2 md:gap-2.5">
                  {gallery.slice(1, 5).map((img, idx) => {
                    const realIndex = idx + 1;
                    const more = gallery.length - 5;
                    const showMore = realIndex === 4 && more > 0;
                    return (
                      <button
                        key={img.id}
                        type="button"
                        onClick={(e) => openGallery(realIndex, e.currentTarget)}
                        aria-label={img.alt_text || t('gallery.viewImage', { n: realIndex + 1 })}
                        className="group relative overflow-hidden rounded-2xl border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                        style={
                          {
                            borderColor: 'var(--sf-accent-border)',
                            '--tw-ring-color': 'var(--primary-color, #10b981)',
                          } as React.CSSProperties
                        }
                      >
                        <Image
                          src={img.image_url}
                          alt=""
                          aria-hidden
                          fill
                          sizes="300px"
                          className="object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.04]"
                        />
                        {showMore && (
                          <span className="absolute inset-0 flex items-center justify-center bg-black/45 backdrop-blur-[2px] transition-colors group-hover:bg-black/55">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-sm font-semibold text-white ring-1 ring-white/15 backdrop-blur-md">
                              <Images className="h-4 w-4" strokeWidth={2} aria-hidden />
                              {t('gallery.viewAll')}
                              <span className="opacity-80">+{more}</span>
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
            </Reveal>
          )}

          {/* Ankündigungs-Banner (Angebote/Urlaub/Hinweise) — ganz oben, über
              der Galerie. Rendert nichts wenn inaktiv/außerhalb Zeitfenster.
              Im aktiven Wizard ausgeblendet (Fokus auf die Buchung). */}
          {!inActiveWizard && (
            <div className="mx-auto mb-4 w-full max-w-5xl empty:hidden">
              <AnnouncementBanner tenant={tenant} />
            </div>
          )}

          {/* Galerie ist jetzt in den Hero integriert (Foto-Showcase + „Alle
              Fotos"-Button/Thumbnails). Hier nur noch die geteilte Lightbox
              (unsichtbar bis geöffnet) + SEO-Schema — spart die separate
              Galerie-Sektion und damit vertikalen Platz. */}
          {gallery.length > 0 && (
            <GallerySection embedded images={gallery} businessName={tenant.name} />
          )}

          {/* Buchungs-Wizard — zentriert, der primäre CTA. */}
          <div className="mx-auto w-full max-w-5xl">
              <BookingSteps
                key={bookingResetKey}
                services={services}
                tenantId={tenantId || ''}
                timezone={tenant?.timezone || DEFAULT_BUSINESS_TIMEZONE}
                firstDayOfWeek={
                  (tenant as { first_day_of_week?: 0 | 1 })?.first_day_of_week === 0 ? 0 : 1
                }
                recalledCustomer={recalledCustomer}
                businessName={tenant?.name}
                storefrontUrl={(() => {
                  if (typeof window === 'undefined') return undefined;
                  // Canonical storefront URL — `window.location.origin`
                  // captures both slug-routed (eazybooking.de/<slug>)
                  // and custom-domain cases identically without a
                  // second DB lookup. Path is the slug if present.
                  return tenant?.slug
                    ? `${window.location.origin}/${tenant.slug}`
                    : window.location.origin;
                })()}
                onSubmit={handleBookingSubmit}
                successMessage={successMessage}
                onResetParent={resetBooking}
                maxGroupSize={tenant?.max_group_size ?? undefined}
                staffPickPosition={
                  (tenant as { staff_pick_position?: 'first' | 'last' })?.staff_pick_position === 'last'
                    ? 'last'
                    : 'first'
                }
                paymentBadges={paymentBadges}
                giftRedemptionAvailable={hasGiftCards}
                intakeEnabled={!!(tenant as { intake_enabled?: boolean }).intake_enabled}
                pendingPayment={pendingPayment}
                onPaymentBack={() => setPendingPayment(null)}
                pendingChoice={pendingChoice}
                onChoiceConfirm={async (choice) => {
                  // Customer picked from PaymentChoiceStep — server
                  // validates against the booking's frozen policy
                  // snapshot, then either marks on_site or returns a
                  // fresh PaymentIntent to mount Stripe Elements.
                  if (!pendingChoice) return;
                  const r = await fetch(
                    '/api/storefront/confirm-payment-choice',
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        bookingId: pendingChoice.bookingId,
                        customerEmail: pendingChoice.customerEmail,
                        choice,
                      }),
                    },
                  );
                  const d = await r.json();
                  if (!d.success) {
                    throw new Error(d.error || 'Choice could not be confirmed');
                  }
                  if (choice === 'on_site') {
                    // Done — confirmation rendered.
                    setPendingChoice(null);
                    setSuccessMessage(
                      t('bookingSuccess', { name: pendingChoice.customerEmail.split('@')[0] }),
                    );
                  } else {
                    // Online payment — hand off to PaymentStep.
                    setPendingChoice(null);
                    setPendingPayment({
                      bookingId: pendingChoice.bookingId,
                      clientSecret: d.payment.clientSecret,
                      connectedAccountId: d.payment.connectedAccountId,
                      amount: d.payment.amount,
                      currency: d.payment.currency,
                    });
                  }
                }}
                onChoiceBack={() => setPendingChoice(null)}
                onHeroCollapseChange={setInActiveWizard}
              />
            </div>

            {/* Trust-Strip direkt unter dem Buchungs-CTA — räumt letzte Zweifel,
                nur wahrheitskonforme, tenant-unabhängige Aussagen. Icon-frei,
                cleane Typo mit dezentem Trennpunkt (F500-2026). */}
            <div className="mx-auto mt-4 flex w-full max-w-5xl flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-500">
              <span>{t('trust.noAccount')}</span>
              <span aria-hidden className="text-gray-300">·</span>
              {/* „Sofortige Bestätigung" nur bei aktivierter Auto-Bestätigung
                  wahr — sonst bestätigt der Tenant manuell → ehrlich „Bestätigung
                  per E-Mail". */}
              <span>
                {(tenant as { auto_confirm_bookings?: boolean }).auto_confirm_bookings
                  ? t('trust.instantConfirm')
                  : t('trust.confirmByEmail')}
              </span>
              {/* Dritter Chip erst nach dem Laden der Payment-Badges (kein Swap-
                  Flicker). „Sichere Zahlung" nur bei aktiver Online-Zahlung,
                  sonst „Schnell & einfach". */}
              {paymentBadgesLoaded && (
                <>
                  <span aria-hidden className="text-gray-300">·</span>
                  <span>
                    {Object.values(paymentBadges).some(Boolean)
                      ? t('trust.securePayment')
                      : t('trust.easy')}
                  </span>
                </>
              )}
            </div>

            {/* Info-Bereich UNTER dem Wizard — kein hohes rechtes Sidebar mehr.
                Kompaktes Card-Grid (Kontakt | Öffnungszeiten | Pakete | Gutschein)
                → ausgewogene Optik, der Wizard bleibt der Fokus. */}
            {/* Spaltenzahl nach Karten-Anzahl: 2 (Kontakt+Öffnungszeiten) → 50/50,
                3 → 3-spaltig, 4 (+Pakete +Gutschein) → 2×2. Keine leere Spalte. */}
            <div
              className={`mx-auto mt-8 grid w-full max-w-5xl items-stretch gap-4 sm:grid-cols-2 ${
                2 +
                  (hasOnlinePackages ? 1 : 0) +
                  ((tenant as { gift_cards_enabled?: boolean }).gift_cards_enabled ? 1 : 0) ===
                3
                  ? 'lg:grid-cols-3'
                  : ''
              }`}
            >
              {/* Contact card — no brand duplication, header already shows it */}
              <Reveal className="h-full" delay={0}>
              <section
                className="h-full overflow-hidden rounded-3xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]"
                style={{ borderColor: 'var(--sf-accent-border)' }}
              >
                <div className="mb-4 flex items-center gap-2">
                  <Phone
                    className="h-4 w-4"
                    strokeWidth={2}
                    style={{ color: 'var(--primary-color, #10b981)' }}
                  />
                  <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    {t('contact')}
                  </h2>
                </div>

                <div className="space-y-1">
                  {tenant.address && fullAddress && (
                    <a
                      href={fullAddress}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start gap-3 rounded-xl px-2 py-2 text-sm transition-colors hover:bg-gray-50"
                    >
                      <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" strokeWidth={2} />
                      <span className="min-w-0 flex-1 text-sm text-gray-700">
                        {/* Address text — its own block so the "Route" chip
                            drops onto a clean new line beneath it instead of
                            crowding the city. */}
                        <span className="block break-words">
                          <span className="font-medium text-gray-900">{tenant.address}</span>
                          {tenant.address_additional && (
                            <>
                              <br />
                              {tenant.address_additional}
                            </>
                          )}
                          <br />
                          <span className="tabular-nums">{tenant.zip}</span> {tenant.city}
                          {tenant.state && <>, {tenant.state}</>}
                        </span>
                        <span
                          className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-transform group-hover:scale-[1.02]"
                          style={{
                            color: 'var(--sf-on-accent, #ffffff)',
                            backgroundColor: 'var(--primary-color, #10b981)',
                          }}
                        >
                          <MapPin className="h-3 w-3" strokeWidth={2.5} />
                          {t('directions')}
                        </span>
                      </span>
                    </a>
                  )}

                  {phoneList.map((num) => (
                    <a
                      key={num}
                      href={`tel:${num.replace(/\s+/g, '')}`}
                      className="flex items-center gap-3 rounded-xl px-2 py-2 text-sm transition-colors hover:bg-gray-50"
                    >
                      <Phone className="h-4 w-4 flex-shrink-0 text-gray-400" strokeWidth={2} />
                      <span className="break-all tabular-nums text-gray-900">
                        {num}
                      </span>
                    </a>
                  ))}

                  {tenant.contact_email && (
                    <a
                      href={`mailto:${tenant.contact_email}`}
                      className="flex items-center gap-3 rounded-xl px-2 py-2 text-sm transition-colors hover:bg-gray-50"
                    >
                      <Mail className="h-4 w-4 flex-shrink-0 text-gray-400" strokeWidth={2} />
                      <span className="break-all text-gray-900">{tenant.contact_email}</span>
                    </a>
                  )}
                </div>

                <SocialIcons tenant={tenant} />
              </section>
              </Reveal>

              {/* Business hours */}
              <Reveal className="h-full" delay={70}>
              <section
                className="h-full overflow-hidden rounded-3xl border border-gray-200/80 bg-white p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)]"
                style={{ borderColor: 'var(--sf-accent-border)' }}
              >
                <div className="mb-4 flex items-center gap-2">
                  <Clock
                    className="h-4 w-4"
                    strokeWidth={2}
                    style={{ color: 'var(--primary-color, #10b981)' }}
                  />
                  <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                    {t('businessHours')}
                  </h2>
                </div>
                {businessHours.length > 0 ? (
                  <>
                    <StatusBadge className="mb-3" />
                    <ul className="space-y-0.5">
                      {dayOrder.map((dayNum) => {
                        const hourForDay = businessHours.find((h) => h.day_of_week === dayNum);
                        const dayData = hourForDay || {
                          tenant_id: tenant?.id || '',
                          day_of_week: dayNum,
                          day_name: '',
                          is_open: false,
                          open_time: null,
                          close_time: null,
                        };
                        const formatted = formatBusinessHourForDisplay(dayData);
                        const isToday = dayNum === todayDow;
                        return (
                          <li
                            key={dayNum}
                            className="flex items-center justify-between rounded-lg px-2 py-1.5"
                            style={
                              isToday
                                ? { backgroundColor: themedTint(accent, 8) }
                                : undefined
                            }
                          >
                            <span
                              className={`text-sm ${
                                isToday ? 'font-semibold text-gray-900' : 'text-gray-700'
                              }`}
                            >
                              {formatted.day}
                            </span>
                            <span
                              className={`text-sm tabular-nums ${
                                formatted.isOpen
                                  ? isToday
                                    ? 'font-semibold'
                                    : 'font-medium'
                                  : 'text-gray-400'
                              }`}
                              style={
                                formatted.isOpen
                                  ? { color: 'var(--primary-color, #10b981)' }
                                  : undefined
                              }
                            >
                              {formatted.hours}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                ) : (
                  <p className="py-4 text-center text-sm text-gray-500">
                    {t('noServicesAvailable')}
                  </p>
                )}
              </section>
              </Reveal>

              {/* Mehrfachkarten online kaufen — nur mounten, wenn der initiale
                  Payload online-verkaufbare Pakete meldet (kein Eager-Fetch).
                  Revenue-Upsell bewusst NACH den Trust-Blöcken (Upsell-zuerst
                  wirkt pushy + senkt Erstkunden-Conversion). */}
              {tenantId && hasOnlinePackages && (
                <Reveal className="h-full" delay={140}>
                  <PackagePurchaseSection tenantId={tenantId} primaryColor={tenant.primary_color || undefined} />
                </Reveal>
              )}

              {/* Gutschein kaufen — nur mounten, wenn der Tenant Gutscheine
                  aktiviert hat (Flag steckt schon im Tenant-Payload). Anker-id für
                  den Header-CTA „🎁 Gutschein" (Smooth-Scroll). */}
              {tenantId && (tenant as { gift_cards_enabled?: boolean }).gift_cards_enabled && (
                <Reveal className="h-full" delay={210}>
                  <div id="gift-cards" className="h-full scroll-mt-24">
                    <GiftCardPurchaseSection tenantId={tenantId} primaryColor={tenant.primary_color || undefined} />
                  </div>
                </Reveal>
              )}
            </div>
        </main>

        {/* Footer — hidden in embed mode (host site provides its own
            legal footer; we still expose Impressum/Privacy as buttons
            elsewhere if the embedding page requires them). */}
        {!embed && (
        <footer className="mt-12 border-t border-gray-200/70">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
              <p className="text-xs text-gray-500">
                &copy; {new Date().getFullYear()} {tenant.name} · {t('allRightsReserved')}
              </p>
              <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Legal">
                <button
                  type="button"
                  onClick={() => setShowImpressum(true)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                  <FileText className="h-3.5 w-3.5" strokeWidth={2} />
                  {t('impressum')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPrivacyPolicy(true)}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                  <Shield className="h-3.5 w-3.5" strokeWidth={2} />
                  {t('privacy')}
                </button>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                >
                  <Cookie className="h-3.5 w-3.5" strokeWidth={2} />
                  {t('cookieSettings')}
                </button>
              </nav>
            </div>

            {/* Powered by — quiet, single-line (geteilte Markup, s. poweredByLink) */}
            <div className="mt-6 flex justify-center">
              {poweredByLink}
            </div>
          </div>
        </footer>
        )}

        {/* Modal Dialogs */}
        <Dialog open={showImpressum} onOpenChange={setShowImpressum}>
          <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>{t('impressum')}</span>
              </DialogTitle>
              <DialogDescription>
                {t('impressumDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="prose max-w-none">
              <Impressum tenant={tenant} />
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
          <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5" />
                <span>{t('privacy')}</span>
              </DialogTitle>
              <DialogDescription>
                {t('privacyDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="prose max-w-none">
              <PrivacyPolicy tenant={tenant} />
            </div>
          </DialogContent>
        </Dialog>

        {/* Cookie Banner - Tenant-specific */}
        {/* Cookie banner is host-page's responsibility in embed mode —
            running our own would stack with the parent's banner. */}
        {!embed && tenant && <StorefrontCookieBanner tenant={tenant} />}
      </div>
    </ThemeProvider>
    </StorefrontFormattersProvider>
  );
}

/* ------------------------- Subcomponents ------------------------- */

interface SocialIconsProps {
  tenant: Pick<
    Tenant,
    'instagram_url' | 'facebook_url' | 'twitter_url' | 'youtube_url' | 'tiktok_url' | 'linkedin_url'
  >;
}

function SocialIcons({ tenant }: SocialIconsProps) {
  const links: Array<{ url?: string | null; label: string; hoverColor: string; path: string }> = [
    {
      url: tenant.instagram_url,
      label: 'Instagram',
      hoverColor: 'hover:text-pink-500',
      path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
    },
    {
      url: tenant.facebook_url,
      label: 'Facebook',
      hoverColor: 'hover:text-blue-600',
      path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    },
    {
      url: tenant.twitter_url,
      label: 'X',
      hoverColor: 'hover:text-gray-900',
      path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    },
    {
      url: tenant.youtube_url,
      label: 'YouTube',
      hoverColor: 'hover:text-red-600',
      path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
    },
    {
      url: tenant.tiktok_url,
      label: 'TikTok',
      hoverColor: 'hover:text-gray-900',
      path: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
    },
    {
      url: tenant.linkedin_url,
      label: 'LinkedIn',
      hoverColor: 'hover:text-blue-700',
      path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
    },
  ];

  const present = links.filter((l) => l.url);
  if (present.length === 0) return null;

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <div className="flex items-center gap-1.5">
        {present.map((link) => (
          <a
            key={link.label}
            href={link.url!}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
            className={`flex h-11 w-11 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-50 ${link.hoverColor}`}
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d={link.path} />
            </svg>
          </a>
        ))}
      </div>
    </div>
  );
}
